import React, { useState, useRef, useEffect } from 'react';
import { Play, Pause, Volume2, VolumeX, Maximize2 } from 'lucide-react';

/**
 * AiEmployeeHeroVideoCard
 * ---------------------------------------------------------------------------
 * Rounded Cloudflare Stream video card used inside the AI Employee page hero.
 * Mirrors the "Enterprise AI In Action" reference styling:
 *   - 16:9 aspect ratio, rounded-2xl corners, soft shadow
 *   - Autoplay + muted + loop background playback
 *   - Compact bottom-right controls: play/pause, mute, fullscreen
 *
 * Uses the Cloudflare Stream Player SDK to drive playback on the iframe.
 * Scoped to this single hero — no shared component behavior is altered.
 */
const AiEmployeeHeroVideoCard = ({ cloudflareVideoId }) => {
  const iframeRef = useRef(null);
  const playerRef = useRef(null);
  const wrapperRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isMuted, setIsMuted] = useState(true);

  // Wire the Cloudflare Stream Player SDK to the iframe.
  useEffect(() => {
    if (!cloudflareVideoId) return;

    const initPlayer = () => {
      if (iframeRef.current && window.Stream) {
        playerRef.current = window.Stream(iframeRef.current);
      }
    };

    if (window.Stream) {
      initPlayer();
      return;
    }

    let script = document.querySelector('script[data-cloudflare-stream-sdk]');
    if (!script) {
      script = document.createElement('script');
      script.src = 'https://embed.cloudflarestream.com/embed/sdk.latest.js';
      script.setAttribute('data-cloudflare-stream-sdk', 'true');
      document.body.appendChild(script);
    }
    script.addEventListener('load', initPlayer);
    return () => script.removeEventListener('load', initPlayer);
  }, [cloudflareVideoId]);

  const togglePlayPause = () => {
    if (!playerRef.current) return;
    if (isPlaying) {
      playerRef.current.pause();
    } else {
      playerRef.current.play().catch(() => {});
    }
    setIsPlaying(!isPlaying);
  };

  const toggleMute = () => {
    if (!playerRef.current) return;
    const nextMuted = !isMuted;
    try {
      playerRef.current.muted = nextMuted;
    } catch (_) {
      // no-op — SDK not yet ready
    }
    setIsMuted(nextMuted);
  };

  const enterFullscreen = () => {
    const el = wrapperRef.current;
    if (!el) return;
    const req =
      el.requestFullscreen ||
      el.webkitRequestFullscreen ||
      el.msRequestFullscreen;
    if (req) req.call(el);
  };

  const controlBtn =
    'w-9 h-9 rounded-md bg-black/60 backdrop-blur-sm hover:bg-black/80 flex items-center justify-center transition-colors duration-200';
  const controlIcon = 'w-4 h-4 text-white';

  return (
    <div
      ref={wrapperRef}
      className="relative w-full overflow-hidden rounded-2xl bg-[#E8E4DC]"
      style={{ aspectRatio: '16 / 9', boxShadow: '0 20px 40px rgba(0,0,0,0.08)' }}
      data-testid="ai-employee-hero-video-card"
    >
      <iframe
        ref={iframeRef}
        title="AI Employee Hero Video"
        src={`https://customer-q87c1i5e2o40bvjt.cloudflarestream.com/${cloudflareVideoId}/iframe?muted=true&preload=true&loop=true&autoplay=true&poster=https%3A%2F%2Fcustomer-q87c1i5e2o40bvjt.cloudflarestream.com%2F${cloudflareVideoId}%2Fthumbnails%2Fthumbnail.jpg%3Ftime%3D%26height%3D600&controls=false`}
        className="absolute inset-0 w-full h-full"
        style={{ border: 'none' }}
        allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
        allowFullScreen
        data-testid="ai-employee-hero-video-iframe"
      />

      {/* Bottom-right compact controls */}
      <div className="absolute bottom-4 right-4 z-10 flex items-center gap-2">
        <button
          type="button"
          onClick={togglePlayPause}
          aria-label={isPlaying ? 'Pause video' : 'Play video'}
          className={controlBtn}
          data-testid="ai-employee-hero-play-button"
        >
          {isPlaying ? (
            <Pause className={controlIcon} />
          ) : (
            <Play className={controlIcon} />
          )}
        </button>
        <button
          type="button"
          onClick={toggleMute}
          aria-label={isMuted ? 'Unmute video' : 'Mute video'}
          className={controlBtn}
          data-testid="ai-employee-hero-mute-button"
        >
          {isMuted ? (
            <VolumeX className={controlIcon} />
          ) : (
            <Volume2 className={controlIcon} />
          )}
        </button>
        <button
          type="button"
          onClick={enterFullscreen}
          aria-label="Enter fullscreen"
          className={controlBtn}
          data-testid="ai-employee-hero-fullscreen-button"
        >
          <Maximize2 className={controlIcon} />
        </button>
      </div>
    </div>
  );
};

export default AiEmployeeHeroVideoCard;
